import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
    CommandDialog,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandSeparator,
} from "./ui/command";
import { Badge } from "./ui/badge";
import {
    MessageSquare,
    Users,
    Phone,
    MapPin,
    Calendar,
    Search,
} from "lucide-react";

const ESTADOS_CONSULTA = {
    nueva: { label: "Nueva", color: "bg-amber-100 text-amber-700" },
    contactada: { label: "Contactada", color: "bg-blue-100 text-blue-700" },
    presupuesto_enviado: { label: "Presupuesto", color: "bg-purple-100 text-purple-700" },
    reservado_temporal: { label: "Reserva temp.", color: "bg-teal-100 text-teal-700" },
    convertida: { label: "Convertida", color: "bg-green-100 text-green-700" },
    descartada: { label: "Descartada", color: "bg-red-50 text-red-600" },
};

const ESTADOS_PAGO = {
    pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-700" },
    parcial: { label: "Parcial", color: "bg-blue-100 text-blue-700" },
    pagado: { label: "Pagado", color: "bg-green-100 text-green-700" },
};

export default function GlobalSearch({ open, onOpenChange }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState({ consultas: [], clientes: [] });
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Keyboard shortcut Cmd/Ctrl + K
    useEffect(() => {
        const down = (e) => {
            if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                onOpenChange((prev) => !prev);
            }
        };
        document.addEventListener("keydown", down);
        return () => document.removeEventListener("keydown", down);
    }, [onOpenChange]);

    // Debounced search
    const doSearch = useCallback(async (q) => {
        if (!q || q.length < 2) {
            setResults({ consultas: [], clientes: [] });
            return;
        }
        setLoading(true);
        try {
            const response = await api.search(q);
            setResults(response.data);
        } catch (error) {
            console.error("Search error:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => doSearch(query), 300);
        return () => clearTimeout(timer);
    }, [query, doSearch]);

    // Reset when dialog closes
    useEffect(() => {
        if (!open) {
            setQuery("");
            setResults({ consultas: [], clientes: [] });
        }
    }, [open]);

    const handleSelect = (type, id) => {
        onOpenChange(false);
        navigate(`/${type}/${id}`);
    };

    const hasResults = results.consultas.length > 0 || results.clientes.length > 0;
    const showEmpty = query.length >= 2 && !loading && !hasResults;

    return (
        <CommandDialog open={open} onOpenChange={onOpenChange}>
            <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
                <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                    className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                    placeholder="Buscar consultas y clientes..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoFocus
                />
                {loading && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                )}
            </div>
            <CommandList>
                {showEmpty && (
                    <CommandEmpty>
                        No se encontraron resultados para "{query}"
                    </CommandEmpty>
                )}

                {query.length < 2 && (
                    <div className="py-14 text-center text-sm text-muted-foreground">
                        <Search className="mx-auto mb-3 h-10 w-10 opacity-20" />
                        <p>Escribe al menos 2 caracteres para buscar</p>
                        <p className="mt-1 text-xs opacity-70">
                            Busca por nombre, email, teléfono o ubicación
                        </p>
                    </div>
                )}

                {results.consultas.length > 0 && (
                    <CommandGroup heading="Consultas">
                        {results.consultas.map((c) => {
                            const estado = ESTADOS_CONSULTA[c.estado] || { label: c.estado, color: "bg-gray-100 text-gray-700" };
                            return (
                                <CommandItem
                                    key={c.id}
                                    value={`consulta-${c.id}-${c.nombre}`}
                                    onSelect={() => handleSelect("consultas", c.id)}
                                    className="cursor-pointer py-3"
                                >
                                    <div className="flex w-full items-center gap-3">
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                            <MessageSquare className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium truncate">{c.nombre}</span>
                                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${estado.color} border-0`}>
                                                    {estado.label}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                                {c.tipo_evento && <span>{c.tipo_evento}</span>}
                                                {c.telefono && (
                                                    <span className="flex items-center gap-1">
                                                        <Phone className="h-3 w-3" />
                                                        {c.telefono}
                                                    </span>
                                                )}
                                                {c.ubicacion && (
                                                    <span className="hidden sm:flex items-center gap-1">
                                                        <MapPin className="h-3 w-3" />
                                                        {c.ubicacion}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                )}

                {results.consultas.length > 0 && results.clientes.length > 0 && (
                    <CommandSeparator />
                )}

                {results.clientes.length > 0 && (
                    <CommandGroup heading="Clientes">
                        {results.clientes.map((c) => {
                            const pago = ESTADOS_PAGO[c.estado_pago] || { label: c.estado_pago || "—", color: "bg-gray-100 text-gray-700" };
                            return (
                                <CommandItem
                                    key={c.id}
                                    value={`cliente-${c.id}-${c.nombre}`}
                                    onSelect={() => handleSelect("clientes", c.id)}
                                    className="cursor-pointer py-3"
                                >
                                    <div className="flex w-full items-center gap-3">
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                                            <Users className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium truncate">{c.nombre}</span>
                                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${pago.color} border-0`}>
                                                    {pago.label}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                                {c.tipo_evento && <span>{c.tipo_evento}</span>}
                                                {c.fecha_evento && (
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="h-3 w-3" />
                                                        {c.fecha_evento}
                                                    </span>
                                                )}
                                                {c.ubicacion && (
                                                    <span className="hidden sm:flex items-center gap-1">
                                                        <MapPin className="h-3 w-3" />
                                                        {c.ubicacion}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                )}
            </CommandList>

            <div className="border-t px-3 py-2 text-[11px] text-muted-foreground flex items-center justify-between">
                <span>
                    <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
                        ↵
                    </kbd>{" "}
                    seleccionar
                </span>
                <span>
                    <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
                        esc
                    </kbd>{" "}
                    cerrar
                </span>
            </div>
        </CommandDialog>
    );
}
